import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import If from '@components/if/if.tsx';
import Loader from '@components/loader/loader.tsx';
import { DetachIcon } from '@shared/components/icons/svg-icon.tsx';
import { getDeviceStatus } from '@modules/digital-signage/utils/digital-signage.utils.ts';
import {
  DeviceStatusFilterEnum,
  GroupDevicesListProps
} from '@modules/digital-signage/model/group-settings.model.ts';
import DevicesListToolbar from '@modules/digital-signage/components/group-settings/components/group-devices/components/group-devices-list/components/devices-list-toolbar/devices-list-toolbar.tsx';
import GroupDeviceRow from '@modules/digital-signage/components/group-settings/components/group-devices/components/group-devices-list/components/group-device-row/group-device-row.tsx';
import AddDevicesMenu from '@modules/digital-signage/components/group-settings/components/group-devices/components/group-devices-list/components/add-devices-menu/add-devices-menu.tsx';

const GroupDevicesList: React.FC<GroupDevicesListProps> = ({
  selectedDevices,
  devicesById,
  devicesList,
  disabled,
  isLoading,
  isAddDisabled,
  onDevicesChange
}) => {
  const { t } = useTranslation();

  const [query, setQuery] = useState<string>('');
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<DeviceStatusFilterEnum>(
    DeviceStatusFilterEnum.All
  );

  const activeCount = selectedDevices.filter(
    (option) =>
      getDeviceStatus(devicesById.get(option.id)) ===
      DeviceStatusFilterEnum.Active
  ).length;
  const statusCounts = {
    [DeviceStatusFilterEnum.All]: selectedDevices.length,
    [DeviceStatusFilterEnum.Active]: activeCount,
    [DeviceStatusFilterEnum.Inactive]: selectedDevices.length - activeCount
  };
  const isFilterActive = statusFilter !== DeviceStatusFilterEnum.All;

  const shownDevices = useMemo(() => {
    const search = query.trim().toLowerCase();
    return selectedDevices.filter((option) => {
      const device = devicesById.get(option.id);
      const deviceId = device?.deviceId ?? '';
      return (
        (option.title.toLowerCase().includes(search) ||
          deviceId.toLowerCase().includes(search)) &&
        (!isFilterActive || getDeviceStatus(device) === statusFilter)
      );
    });
  }, [selectedDevices, devicesById, query, statusFilter, isFilterActive]);

  const shownCheckedCount = shownDevices.filter((option) =>
    checkedIds.includes(option.id)
  ).length;
  const isAllChecked =
    shownDevices.length > 0 && shownCheckedCount === shownDevices.length;

  const removeDevices = (ids: string[]) => {
    onDevicesChange(
      selectedDevices.filter((option) => !ids.includes(option.id))
    );
    setCheckedIds((current) => current.filter((id) => !ids.includes(id)));
  };

  useEffect(() => {
    setCheckedIds((current) =>
      current.filter((id) => selectedDevices.some((option) => option.id === id))
    );
  }, [selectedDevices]);

  const toggleChecked = (id: string) =>
    setCheckedIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );

  const toggleAllChecked = () => {
    const shownIds = shownDevices.map((option) => option.id);
    setCheckedIds((current) =>
      isAllChecked
        ? current.filter((id) => !shownIds.includes(id))
        : [...new Set([...current, ...shownIds])]
    );
  };

  return (
    <div className="flex flex-col gap-2.5 min-h-0">
      <div className="flex items-center justify-between gap-2">
        <Typography
          variant="body2"
          className="min-w-0 truncate font-semibold! text-(--dark-1)!"
        >
          {t('digital_signage_group_devices_in_group')}{' '}
          {!isLoading && (
            <Typography
              component="span"
              variant="body2"
              className="font-semibold! text-(--gray-2)!"
            >
              ({selectedDevices.length})
            </Typography>
          )}
        </Typography>
        <AddDevicesMenu
          devices={devicesList}
          selectedDevices={selectedDevices}
          disabled={isAddDisabled}
          onDevicesChange={onDevicesChange}
        />
      </div>

      <DevicesListToolbar
        query={query}
        onQueryChange={setQuery}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        statusCounts={statusCounts}
        disabled={disabled || isLoading}
      />

      <div className="flex flex-col min-h-0 rounded-[10px] border border-(--gray-9) overflow-hidden">
        <div className="flex items-center gap-2.5 h-11 px-3 shrink-0 bg-(--gray-11) border-b border-(--gray-9)">
          <Checkbox
            className="primary-checkbox"
            checked={isAllChecked}
            indeterminate={shownCheckedCount > 0 && !isAllChecked}
            disabled={disabled || isLoading || !shownDevices.length}
            onChange={toggleAllChecked}
          />
          <If
            condition={checkedIds.length > 0}
            emptyContent={
              <Typography
                variant="caption"
                className="flex-1 text-xs! font-medium! text-(--gray-2)!"
              >
                {!isLoading &&
                  t('digital_signage_group_select_all', {
                    count: shownDevices.length
                  })}
              </Typography>
            }
          >
            <Typography
              variant="caption"
              className="flex-1 text-xs! font-semibold! text-(--primary)!"
            >
              {t('digital_signage_group_devices_selected', {
                count: checkedIds.length
              })}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => removeDevices(checkedIds)}
              disabled={disabled}
              startIcon={<DetachIcon className="w-3.25! h-3.25!" />}
              className="shrink-0 min-w-0! h-7.5! px-2.5! gap-0! rounded-[10px]! border-(--red-4)! bg-(--white)! hover:bg-(--red-4)/8! text-(--red-4)! [&_.MuiButton-startIcon]:mr-1.5! [&_.MuiButton-startIcon]:ml-0!"
            >
              <Typography
                variant="caption"
                component="span"
                className="font-medium! text-inherit!"
              >
                {t('digital_signage_group_remove_from_group')}
              </Typography>
            </Button>
          </If>
        </div>

        <If
          loading={isLoading}
          loadingContent={
            <div className="flex items-center justify-center py-10">
              <Loader />
            </div>
          }
          condition={shownDevices.length > 0}
          emptyContent={
            <div className="flex flex-col items-center justify-center gap-1 px-4 py-6 text-center">
              <Typography
                variant="body2"
                className="font-semibold! text-(--gray-1)!"
              >
                {t(
                  selectedDevices.length
                    ? 'digital_signage_group_devices_no_match_title'
                    : 'digital_signage_group_devices_empty_title'
                )}
              </Typography>
              <Typography variant="caption" className="text-(--gray-2)!">
                {t(
                  selectedDevices.length
                    ? 'digital_signage_group_devices_no_match_description'
                    : 'digital_signage_group_devices_empty_description'
                )}
              </Typography>
            </div>
          }
        >
          <div className="min-h-0 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {shownDevices.map((option) => (
              <GroupDeviceRow
                key={option.id}
                option={option}
                device={devicesById.get(option.id)}
                checked={checkedIds.includes(option.id)}
                disabled={disabled}
                onToggle={toggleChecked}
                onRemove={(id) => removeDevices([id])}
              />
            ))}
          </div>
        </If>
      </div>
    </div>
  );
};

export default GroupDevicesList;
